import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useLocation } from 'react-router-dom';

import { useCreateChatConversation } from '~/hooks/apis/ai-chat/useCreateChatConversation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { sendConversationItemsStream } from '~/services/AIChatService';
import { getOrder } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import { AiChatContext, ChatMessage, ChatThread, ChatThreadMode, ThreadContextSnapshot } from './AiChatContext';
import { extractDepartmentSelection } from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS } from './chatModes';
import { getChatStorageKey, loadChatState, loadDepartment, saveChatState, saveDepartment } from './chatStorage';
import {
  buildAppContextMessage,
  buildThreadId,
  buildThreadTitle,
  hasMeaningfulContext,
  isEmptyChatThread,
  isSameContextSnapshot,
} from './chatThreadUtils';
import { generateConversationRef } from './conversationRef';
import { useCurrentCustomerId } from './useCurrentCustomerId';

interface AiChatProviderProps {
  children: React.ReactNode;
}

const NEW_CHAT_AFTER_IDLE_MS = 10 * 60 * 1000;

function createEmptyThread(
  openaiConversationId: string,
  mode: ChatThreadMode,
  initialContextSnapshot: ThreadContextSnapshot | null,
): ChatThread {
  const now = Date.now();

  return {
    id: buildThreadId(),
    openaiConversationId,
    conversationRef: generateConversationRef(),
    title: buildThreadTitle(now),
    mode,
    messages: [],
    latestContextSnapshot: initialContextSnapshot,
    createdAt: now,
    updatedAt: now,
  };
}

function hasPriorFraudDecision(messages: Array<ChatMessage>): boolean {
  return messages.some(
    (message) =>
      message.role === 'assistant' &&
      /Decision:\s*(Low fraud concern|Needs manual review|High fraud concern)/i.test(message.content || ''),
  );
}

function upsertError(
  errorsByThreadId: Record<string, string | null>,
  threadId: string,
  error: string | null,
): Record<string, string | null> {
  return {
    ...errorsByThreadId,
    [threadId]: error,
  };
}

function sortThreadsByUpdatedAt(threads: Record<string, ChatThread>, mode?: ChatThreadMode): Array<ChatThread> {
  return Object.values(threads)
    .filter((thread) => !mode || thread.mode === mode)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

function updateAssistantMessage(
  threads: Record<string, ChatThread>,
  threadId: string,
  createdAt: number,
  patch: Partial<ChatMessage>,
): Record<string, ChatThread> {
  const current = threads[threadId];
  if (!current) {
    return threads;
  }

  return {
    ...threads,
    [threadId]: {
      ...current,
      messages: current.messages.map((message) =>
        message.role === 'assistant' && message.createdAt === createdAt ? { ...message, ...patch } : message,
      ),
      updatedAt: Date.now(),
    },
  };
}

function removeAssistantMessage(
  threads: Record<string, ChatThread>,
  threadId: string,
  createdAt: number,
): Record<string, ChatThread> {
  const current = threads[threadId];
  if (!current) {
    return threads;
  }

  return {
    ...threads,
    [threadId]: {
      ...current,
      messages: current.messages.filter(
        (message) => !(message.role === 'assistant' && message.createdAt === createdAt),
      ),
      updatedAt: Date.now(),
    },
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export default function AiChatProvider({ children }: AiChatProviderProps) {
  const storedState = useMemo(() => loadChatState(), []);
  const [storedDepartment, setStoredDepartment] = useState<string | null>(() => loadDepartment());
  const [threads, setThreads] = useState<Record<string, ChatThread>>(storedState.threads);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(storedState.activeThreadId);
  const [isOpen, setIsOpen] = useState(storedState.isOpen);
  const [errorsByThreadId, setErrorsByThreadId] = useState<Record<string, string | null>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [sendingThreadIds, setSendingThreadIds] = useState<Array<string>>([]);

  const createConversationMutation = useCreateChatConversation();
  const { user } = useCurrentUser();
  const location = useLocation();

  const { customerId: urlCustomerId, orderId: urlOrderId } = useCurrentCustomerId();
  const orderCacheRef = useRef<Record<string, { customerId: string; customerName: string }>>({});
  const userNameCacheRef = useRef<Record<string, string>>({});
  const activeStreamControllersRef = useRef(new Map<string, AbortController>());
  const [resolvedCustomer, setResolvedCustomer] = useState<{
    customerId: string;
    customerName: string;
  } | null>(null);

  useEffect(() => {
    if (!urlOrderId) {
      setResolvedCustomer(null);
      return;
    }

    const cached = orderCacheRef.current[urlOrderId];
    if (cached) {
      setResolvedCustomer(cached);
      return;
    }

    let cancelled = false;
    getOrder(urlOrderId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const order = response?.result;
        if (order?.fk_customer_id) {
          const result = {
            customerId: order.fk_customer_id,
            customerName: order.customer_full_name || order.customer_email || 'Unknown',
          };
          orderCacheRef.current[urlOrderId] = result;
          setResolvedCustomer(result);
        }
      })
      .catch(() => {
        // Fall back to null context when order resolution fails.
      });

    return () => {
      cancelled = true;
    };
  }, [urlOrderId]);

  const currentUrlCustomerId = urlCustomerId || resolvedCustomer?.customerId || null;
  const currentUrlOrderId = urlOrderId || null;

  const resolveCustomerName = useCallback(
    async (customerId: string | null): Promise<string | null> => {
      if (!customerId) {
        return null;
      }

      if (resolvedCustomer?.customerId === customerId && resolvedCustomer.customerName) {
        return resolvedCustomer.customerName;
      }

      if (userNameCacheRef.current[customerId]) {
        return userNameCacheRef.current[customerId];
      }

      try {
        const resolvedUser = await UsersService.getUser(customerId);
        const nextName = resolvedUser?.fullName || resolvedUser?.email || 'Unknown';
        userNameCacheRef.current[customerId] = nextName;
        return nextName;
      } catch {
        return null;
      }
    },
    [resolvedCustomer],
  );

  const buildMainContextSnapshot = useCallback(async (): Promise<ThreadContextSnapshot | null> => {
    const customerId = currentUrlCustomerId;
    const customerName = (await resolveCustomerName(customerId)) || resolvedCustomer?.customerName || null;
    const snapshot: ThreadContextSnapshot = {
      customerId,
      customerName,
      orderId: currentUrlOrderId,
      department: storedDepartment,
      mode: 'default',
      page: location.pathname || null,
    };

    return hasMeaningfulContext(snapshot) ? snapshot : null;
  }, [
    currentUrlCustomerId,
    currentUrlOrderId,
    location.pathname,
    resolveCustomerName,
    resolvedCustomer?.customerName,
    storedDepartment,
  ]);

  const [currentContextSnapshot, setCurrentContextSnapshot] = useState<ThreadContextSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;

    buildMainContextSnapshot()
      .then((snapshot) => {
        if (!cancelled) {
          setCurrentContextSnapshot(snapshot);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCurrentContextSnapshot(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [buildMainContextSnapshot]);

  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const activeThreadIdRef = useRef(activeThreadId);
  activeThreadIdRef.current = activeThreadId;

  const debouncedSave = useMemo(
    () =>
      debounce((nextThreads: Record<string, ChatThread>, nextActiveThreadId: string | null, nextIsOpen: boolean) => {
        saveChatState({
          threads: nextThreads,
          activeThreadId: nextActiveThreadId,
          isOpen: nextIsOpen,
        });
      }, 300),
    [],
  );

  useEffect(() => {
    debouncedSave(threads, activeThreadId, isOpen);
  }, [activeThreadId, debouncedSave, isOpen, threads]);

  useEffect(() => {
    saveChatState({
      threads: threadsRef.current,
      activeThreadId: activeThreadIdRef.current,
      isOpen,
    });
  }, [isOpen]);

  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  useEffect(
    () => () => {
      activeStreamControllersRef.current.forEach((controller) => controller.abort());
      activeStreamControllersRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== getChatStorageKey()) {
        return;
      }

      const nextState = loadChatState();
      setThreads(nextState.threads);
      setActiveThreadId(nextState.activeThreadId);
      setIsOpen(nextState.isOpen);
    }

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const createThread = useCallback(
    async (
      mode: ChatThreadMode = 'default',
      initialContextSnapshot: ThreadContextSnapshot | null = null,
    ): Promise<string | null> => {
      const reusableThread = sortThreadsByUpdatedAt(threadsRef.current, mode).find(isEmptyChatThread);

      if (reusableThread) {
        setThreads((prev) => {
          const current = prev[reusableThread.id];
          if (!current) {
            return prev;
          }

          return {
            ...prev,
            [reusableThread.id]: {
              ...current,
              latestContextSnapshot: initialContextSnapshot,
            },
          };
        });
        setActiveThreadId(reusableThread.id);
        setErrorsByThreadId((prev) => upsertError(prev, reusableThread.id, null));
        setGlobalError(null);

        return reusableThread.id;
      }

      const response = await createConversationMutation.mutateAsync();
      const thread = createEmptyThread(response.conversationId, mode, initialContextSnapshot);

      setThreads((prev) => ({
        ...prev,
        [thread.id]: thread,
      }));

      setActiveThreadId(thread.id);
      setErrorsByThreadId((prev) => upsertError(prev, thread.id, null));
      setGlobalError(null);

      return thread.id;
    },
    [createConversationMutation],
  );

  const openChat = useCallback(async () => {
    const currentActiveThread =
      activeThreadIdRef.current != null ? threadsRef.current[activeThreadIdRef.current] || null : null;

    const isStaleThread = (thread: ChatThread | null): boolean =>
      thread != null && Date.now() - thread.updatedAt > NEW_CHAT_AFTER_IDLE_MS;

    if (currentActiveThread?.mode === 'default' && isStaleThread(currentActiveThread)) {
      try {
        await createThread('default', currentContextSnapshot);
      } catch (error) {
        setGlobalError(error instanceof Error ? error.message : 'Failed to start a new chat. Please try again.');
      }

      setIsOpen(true);
      return;
    }

    if (currentActiveThread?.mode === 'default') {
      setActiveThreadId(currentActiveThread.id);
      setGlobalError(null);
      setIsOpen(true);
      return;
    }

    const defaultThreads = sortThreadsByUpdatedAt(threadsRef.current, 'default');

    if (defaultThreads.length > 0) {
      if (isStaleThread(defaultThreads[0])) {
        try {
          await createThread('default', currentContextSnapshot);
        } catch (error) {
          setGlobalError(error instanceof Error ? error.message : 'Failed to start a new chat. Please try again.');
        }

        setIsOpen(true);
        return;
      }

      setActiveThreadId(defaultThreads[0].id);
      setGlobalError(null);
    } else {
      try {
        await createThread('default', currentContextSnapshot);
      } catch (error) {
        setGlobalError(error instanceof Error ? error.message : 'Failed to start a new chat. Please try again.');
      }
    }

    setIsOpen(true);
  }, [createThread, currentContextSnapshot]);

  const stopStreaming = useCallback((threadId: string) => {
    const controller = activeStreamControllersRef.current.get(threadId);
    if (controller) {
      controller.abort();
      activeStreamControllersRef.current.delete(threadId);
    }

    setSendingThreadIds((prev) => prev.filter((id) => id !== threadId));
  }, []);

  const closeChat = useCallback(() => {
    if (activeThreadIdRef.current) {
      stopStreaming(activeThreadIdRef.current);
    }

    setIsOpen(false);
  }, [stopStreaming]);

  const switchThread = useCallback(
    (threadId: string) => {
      if (activeThreadIdRef.current && activeThreadIdRef.current !== threadId) {
        stopStreaming(activeThreadIdRef.current);
      }

      setActiveThreadId(threadId);
      setGlobalError(null);
    },
    [stopStreaming],
  );

  const deleteThread = useCallback(
    (threadId: string) => {
      stopStreaming(threadId);

      setThreads((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });

      setErrorsByThreadId((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });

      setActiveThreadId((current) => {
        if (current !== threadId) {
          return current;
        }

        const remainingThreads = sortThreadsByUpdatedAt(
          Object.fromEntries(Object.entries(threadsRef.current).filter(([id]) => id !== threadId)),
          'default',
        );
        return remainingThreads[0]?.id || null;
      });
    },
    [stopStreaming],
  );

  const sendMessage = useCallback(
    async (threadId: string, content: string, contextSnapshot: ThreadContextSnapshot | null) => {
      const thread = threadsRef.current[threadId];
      if (!thread) {
        return;
      }

      const now = Date.now();
      const selectedDepartment = extractDepartmentSelection(content, DEPARTMENT_OPTIONS);

      if (selectedDepartment && selectedDepartment !== storedDepartment) {
        saveDepartment(selectedDepartment);
        setStoredDepartment(selectedDepartment);
      }

      const effectiveContextSnapshot =
        contextSnapshot != null
          ? {
              ...contextSnapshot,
              department: selectedDepartment || contextSnapshot.department || storedDepartment || null,
            }
          : null;

      const nextMessages: Array<ChatMessage> = [];
      const requestItems: Array<{ role: 'user'; content: string; kind: 'context' | 'user' }> = [];

      if (effectiveContextSnapshot && hasMeaningfulContext(effectiveContextSnapshot)) {
        const hasContextMessage = thread.messages.some((message) => message.role === 'context');

        if (!hasContextMessage || !isSameContextSnapshot(thread.latestContextSnapshot, effectiveContextSnapshot)) {
          nextMessages.push({
            role: 'context',
            content: buildAppContextMessage(effectiveContextSnapshot),
            createdAt: now,
            contextSnapshot: effectiveContextSnapshot,
          });
          requestItems.push({
            role: 'user',
            content: buildAppContextMessage(effectiveContextSnapshot),
            kind: 'context',
          });
        }
      }

      nextMessages.push({
        role: 'user',
        content,
        createdAt: now + nextMessages.length,
      });
      requestItems.push({
        role: 'user',
        content,
        kind: 'user',
      });

      const assistantCreatedAt = now + nextMessages.length;
      const assistantPlaceholder: ChatMessage = {
        role: 'assistant',
        content: '',
        createdAt: assistantCreatedAt,
      };
      let streamedContent = '';

      setThreads((prev) => {
        const current = prev[threadId];
        if (!current) {
          return prev;
        }

        return {
          ...prev,
          [threadId]: {
            ...current,
            messages: [...current.messages, ...nextMessages, assistantPlaceholder],
            latestContextSnapshot:
              nextMessages.find((message) => message.role === 'context')?.contextSnapshot ||
              current.latestContextSnapshot,
            updatedAt: now,
          },
        };
      });
      setErrorsByThreadId((prev) => upsertError(prev, threadId, null));
      setSendingThreadIds((prev) => (prev.includes(threadId) ? prev : [...prev, threadId]));

      const controller = new AbortController();
      activeStreamControllersRef.current.set(threadId, controller);

      try {
        const response = await sendConversationItemsStream(
          {
            conversationId: thread.openaiConversationId,
            context: {
              conversationRef: thread.conversationRef,
              agentId: user?.id_member || null,
              agentEmail: user?.email || null,
              customerId: effectiveContextSnapshot?.customerId || null,
              customerName: effectiveContextSnapshot?.customerName || null,
              orderId: effectiveContextSnapshot?.orderId || null,
              department: effectiveContextSnapshot?.department || null,
              mode: effectiveContextSnapshot?.mode || thread.mode,
              page: effectiveContextSnapshot?.page || null,
              hasPriorFraudDecision: hasPriorFraudDecision(thread.messages),
            },
            items: requestItems,
          },
          {
            onTokenBatch: (text) => {
              streamedContent += text;
              setThreads((prev) =>
                updateAssistantMessage(prev, threadId, assistantCreatedAt, { content: streamedContent }),
              );
            },
            onStatus: () => {
              // Parsed for protocol compatibility. Visible tool-loading UI will be added separately.
            },
            onToolCall: () => {
              // Tool metadata is applied on message_end so reviewed-tool cards appear after completion.
            },
          },
          { signal: controller.signal },
        );

        setThreads((prev) => {
          const current = prev[threadId];
          if (!current) {
            return prev;
          }

          return updateAssistantMessage(prev, threadId, assistantCreatedAt, {
            content: response.content,
            toolCalls: response.toolCalls.length > 0 ? response.toolCalls : undefined,
            responseId: response.responseId || undefined,
          });
        });
      } catch (error) {
        if (!streamedContent) {
          setThreads((prev) => removeAssistantMessage(prev, threadId, assistantCreatedAt));
        }

        if (isAbortError(error)) {
          return;
        }

        setErrorsByThreadId((prev) =>
          upsertError(
            prev,
            threadId,
            error instanceof Error ? error.message : 'Failed to get a response. Please try again.',
          ),
        );
      } finally {
        if (activeStreamControllersRef.current.get(threadId) === controller) {
          activeStreamControllersRef.current.delete(threadId);
          setSendingThreadIds((prev) => prev.filter((id) => id !== threadId));
        }
      }
    },
    [storedDepartment, user?.email, user?.id_member],
  );

  const activeThread = activeThreadId ? threads[activeThreadId] || null : null;

  const value = useMemo(
    () => ({
      isOpen,
      threads,
      activeThreadId,
      activeThread,
      sendingThreadIds,
      errorsByThreadId,
      globalError,
      currentUrlCustomerId,
      currentUrlOrderId,
      currentContextSnapshot,
      openChat,
      closeChat,
      createThread,
      sendMessage,
      stopStreaming,
      switchThread,
      deleteThread,
    }),
    [
      activeThread,
      activeThreadId,
      closeChat,
      createThread,
      currentContextSnapshot,
      currentUrlCustomerId,
      currentUrlOrderId,
      deleteThread,
      errorsByThreadId,
      globalError,
      isOpen,
      openChat,
      sendMessage,
      sendingThreadIds,
      stopStreaming,
      switchThread,
      threads,
    ],
  );

  return <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>;
}
